import React from 'react';
import { Link } from 'react-router';
import { Brain, Play, BarChart3, FileText, Zap, Radio, ExternalLink } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { lscPath } from '../lsc/routing';
import {
  BUILD_ENGINE_ACCESS_MODE,
  resolveEngineWaitlistHref,
  SHOW_FILM_UPLOAD_ENTRY_POINTS,
  SHOW_STREAM_CENTER_ENTRY_POINTS,
} from '../utils/engineAccess';

const Home: React.FC = () => {
  const closedBeta = BUILD_ENGINE_ACCESS_MODE !== 'open';
  const waitlistHref = resolveEngineWaitlistHref();
  const waitlistExternal =
    waitlistHref.startsWith('http') || waitlistHref.startsWith('mailto:');

  const uploadFeatures = [
    {
      icon: Play,
      title: 'Video Analysis',
      description: 'Upload MP4 files or paste Twitch/YouTube links for instant analysis',
    },
    {
      icon: Brain,
      title: 'AI-Powered Insights',
      description: 'GPT-4o Vision analyzes gameplay frames for Madden-specific scouting tendencies',
    },
    {
      icon: BarChart3,
      title: 'Background Processing',
      description: 'Uploads run through our Cloud pipeline — get notified when your report is ready',
    },
    {
      icon: FileText,
      title: 'Scouting Reports',
      description: 'Run/pass splits, formations, situational tendencies, and timestamped evidence logs',
    },
  ];

  const streamCenterFeatures = [
    {
      icon: Radio,
      title: 'League Stream Center',
      description: 'Commissioners manage leagues, members, schedules, and live streams in one place',
    },
    {
      icon: Brain,
      title: 'AI-Powered Insights',
      description: 'Film Intelligence analyzes gameplay for Madden-specific scouting tendencies',
    },
    {
      icon: BarChart3,
      title: 'Live Game Context',
      description: 'Track league streams and verified live games as VOD workflows come online',
    },
    {
      icon: FileText,
      title: 'Scouting Reports',
      description: 'Run/pass splits, formations, situational tendencies, and timestamped evidence logs',
    },
  ];

  const features = SHOW_FILM_UPLOAD_ENTRY_POINTS ? uploadFeatures : streamCenterFeatures;

  const howItWorksUpload = [
    {
      step: '1',
      title: 'Upload Video',
      description: 'Drop your MP4 file or paste a Twitch/YouTube link',
    },
    {
      step: '2',
      title: 'AI Analysis',
      description: 'GPT-4o Vision samples key frames and builds tendency reports for your team',
    },
    {
      step: '3',
      title: 'Get Report',
      description: 'View your scouting report online with confidence scores and evidence logs',
    },
  ];

  const howItWorksStreamCenter = [
    {
      step: '1',
      title: 'Open Stream Center',
      description: 'Visit League Stream Center to manage your league and live streams',
    },
    {
      step: '2',
      title: 'AI Analysis',
      description: 'Film Intelligence builds tendency reports as verified game footage is available',
    },
    {
      step: '3',
      title: 'Get Report',
      description: 'View scouting reports online with confidence scores and evidence logs',
    },
  ];

  const howItWorks = SHOW_FILM_UPLOAD_ENTRY_POINTS ? howItWorksUpload : howItWorksStreamCenter;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Hero Section */}
      <div className="text-center py-16">
        <div className="flex justify-center mb-8">
          <BrandLogo variant="icon" className="h-24 w-24 md:h-28 md:w-28 rounded-2xl" />
        </div>

        <h1 className="text-5xl font-bold mb-6" style={{ color: '#A259FF', textShadow: '0 0 8px #A259FF' }}>
          Welcome to Scoutlytics
        </h1>

        <p className="text-xl text-secondary-600 dark:text-secondary-300 mb-4 max-w-3xl mx-auto">
          AI-powered scouting platform that analyzes Madden gameplay footage and generates detailed team scouting reports with run/pass tendencies, formations, and situational breakdowns.
        </p>

        {SHOW_STREAM_CENTER_ENTRY_POINTS && (
          <p
            className="text-sm text-secondary-500 dark:text-secondary-400 mb-8 max-w-2xl mx-auto"
            data-testid="home-engine-beta-notice"
          >
            {closedBeta
              ? 'Film Intelligence is in private beta. League Stream Center is available first.'
              : 'League Stream Center is the primary entry point for commissioners and league members.'}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {SHOW_FILM_UPLOAD_ENTRY_POINTS ? (
            <Link to="/upload" className="btn-primary inline-flex items-center space-x-2" data-testid="home-upload-cta">
              <Zap className="h-5 w-5" />
              <span>Start Analysis</span>
            </Link>
          ) : SHOW_STREAM_CENTER_ENTRY_POINTS ? (
            <>
              <Link
                to={lscPath()}
                className="btn-primary inline-flex items-center space-x-2"
                data-testid="home-lsc-cta"
              >
                <Radio className="h-5 w-5" />
                <span>{closedBeta ? 'Visit League Stream Center' : 'Open Stream Center'}</span>
              </Link>
              {closedBeta &&
                (waitlistExternal ? (
                  <a
                    href={waitlistHref}
                    className="btn-secondary inline-flex items-center space-x-2"
                    data-testid="home-waitlist-cta"
                    rel={waitlistHref.startsWith('http') ? 'noopener noreferrer' : undefined}
                    target={waitlistHref.startsWith('http') ? '_blank' : undefined}
                  >
                    <ExternalLink className="h-5 w-5" />
                    <span>Join Film Intelligence Waitlist</span>
                  </a>
                ) : (
                  <Link
                    to={waitlistHref}
                    className="btn-secondary inline-flex items-center space-x-2"
                    data-testid="home-waitlist-cta"
                  >
                    <ExternalLink className="h-5 w-5" />
                    <span>Join Film Intelligence Waitlist</span>
                  </Link>
                ))}
            </>
          ) : null}
          {SHOW_FILM_UPLOAD_ENTRY_POINTS && (
            <button className="btn-secondary inline-flex items-center space-x-2">
              <Play className="h-5 w-5" />
              <span>Watch Demo</span>
            </button>
          )}
        </div>
      </div>

      {/* Features Section */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 py-16">
        {features.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <div key={index} className="card text-center border-2 border-primary-400 dark:border-primary-400 bg-white dark:bg-gradient-to-br dark:from-primary-400/20 dark:to-secondary-900/80 rounded-xl shadow-md p-6 transition-colors">
              <div className="bg-primary-100 dark:bg-primary-400/30 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Icon className="h-8 w-8 text-primary-600 dark:text-primary-200" />
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                {feature.title}
              </h3>
              <p className="text-secondary-600 dark:text-white">
                {feature.description}
              </p>
            </div>
          );
        })}
      </div>

      {/* How It Works Section */}
      <div className="py-16">
        <h2 className="text-3xl font-bold text-center text-secondary-900 dark:text-white mb-12">
          How It Works
        </h2>

        <div className="grid md:grid-cols-3 gap-8">
          {howItWorks.map((item) => (
            <div
              key={item.step}
              className="text-center border-2 border-primary-400 dark:border-primary-400 bg-white dark:bg-gradient-to-br dark:from-primary-400/20 dark:to-secondary-900/80 rounded-xl shadow-md p-6 transition-colors"
            >
              <div className="bg-primary-600 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                {item.step}
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
                {item.title}
              </h3>
              <p className="text-secondary-600 dark:text-white">
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Home;
